import { ref, Ref } from 'vue';
import {
  EtradeApiRequestBodyTemplate,
  daysOfWeek
} from '../models/EtradeApiModels';
import { DayOfWeek, RepeatFrequency } from '@etp/etp-gain-bff-client/axios';

interface TimerConditions {
  enabledDays?: DayOfWeek[];
}

export const useRequestBodyTemplates = (
  emitUpdate: () => void,
  timerConditions: Ref<TimerConditions>
) => {
  const selectedDays = ref<number[]>([]);
  const activeDay = ref('1');
  const daysRequestBodyTemplates = ref<
    Record<number, EtradeApiRequestBodyTemplate>
  >({});

  const singleRequestBodyTemplate = ref<EtradeApiRequestBodyTemplate>({
    reportName: '',
    reportViewName: '',
    account: 'ALL',
    beginDate: '',
    beginDateType: 'current',
    endDate: '',
    endDateType: 'current',
    filters: []
  });

  const getDayLabel = (dayValue: number) => {
    const day = daysOfWeek.find(d => d.value === dayValue);
    return day ? day.label : '';
  };

  const selectAllDays = () => {
    daysOfWeek.forEach(day => {
      const dayValue = day.value;
      if (!selectedDays.value.includes(dayValue)) {
        selectedDays.value.push(dayValue);

        const existingSettings = Object.values(daysRequestBodyTemplates.value);
        const templateSetting =
          existingSettings.length > 0 ? existingSettings[0] : null;

        daysRequestBodyTemplates.value[dayValue] = {
          dayOfWeek: dayValue,
          reportName: templateSetting?.reportName || '',
          reportViewName: templateSetting?.reportViewName || '',
          account: 'ALL',
          beginDate: '',
          beginDateType: 'current',
          endDate: '',
          endDateType: 'current',
          filters: []
        };

        if (
          !timerConditions.value.enabledDays?.includes(dayValue as DayOfWeek)
        ) {
          timerConditions.value.enabledDays?.push(dayValue as DayOfWeek);
        }
      }
    });

    selectedDays.value.sort((a, b) => a - b);
    if (selectedDays.value.length > 0) {
      activeDay.value = String(selectedDays.value[0]);
    }

    emitUpdate();
  };

  const toggleDay = (dayValue: number) => {
    const index = selectedDays.value.indexOf(dayValue);

    if (index > -1) {
      selectedDays.value.splice(index, 1);
      delete daysRequestBodyTemplates.value[dayValue];

      const enabledIndex = timerConditions.value.enabledDays?.indexOf(
        dayValue as DayOfWeek
      );
      if (enabledIndex !== undefined && enabledIndex > -1) {
        timerConditions.value.enabledDays?.splice(enabledIndex, 1);
      }

      if (
        selectedDays.value.length > 0 &&
        activeDay.value === String(dayValue)
      ) {
        activeDay.value = String(selectedDays.value[0]);
      }
    } else {
      selectedDays.value.push(dayValue);
      selectedDays.value.sort((a, b) => a - b);

      const existingSettings = Object.values(daysRequestBodyTemplates.value);
      const templateSetting =
        existingSettings.length > 0 ? existingSettings[0] : null;

      daysRequestBodyTemplates.value[dayValue] = {
        dayOfWeek: dayValue,
        reportName: templateSetting?.reportName || '',
        reportViewName: templateSetting?.reportViewName || '',
        account: 'ALL',
        beginDate: '',
        beginDateType: 'current',
        endDate: '',
        endDateType: 'current',
        filters: []
      };

      if (!timerConditions.value.enabledDays?.includes(dayValue as DayOfWeek)) {
        timerConditions.value.enabledDays?.push(dayValue as DayOfWeek);
      }

      activeDay.value = String(dayValue);
    }

    emitUpdate();
  };

  const copyConfigToDay = (
    sourceDayValue: number,
    targetDayValue: number | 'all'
  ) => {
    const sourceSettings = daysRequestBodyTemplates.value[sourceDayValue];
    if (!sourceSettings) return;

    if (targetDayValue === 'all') {
      selectedDays.value.forEach(dayValue => {
        if (dayValue !== sourceDayValue) {
          daysRequestBodyTemplates.value[dayValue] = {
            ...JSON.parse(JSON.stringify(sourceSettings)),
            dayOfWeek: dayValue
          };
        }
      });
    } else {
      daysRequestBodyTemplates.value[targetDayValue] = {
        ...JSON.parse(JSON.stringify(sourceSettings)),
        dayOfWeek: targetDayValue
      };
    }

    emitUpdate();
  };

  const addFilter = (setting: EtradeApiRequestBodyTemplate) => {
    if (!setting) return;

    if (!setting.filters) {
      setting.filters = [];
    }
    setting.filters.push({
      name: '',
      operator: '',
      values: ''
    });
    emitUpdate();
  };

  const deleteFilter = (
    setting: EtradeApiRequestBodyTemplate,
    filterIndex: number
  ) => {
    if (!setting) return;

    setting.filters.splice(filterIndex, 1);
    emitUpdate();
  };

  const updateApiRequestBodyField = (
    setting: EtradeApiRequestBodyTemplate,
    field: string,
    value: any
  ) => {
    setting[field] = value;
    emitUpdate();
  };

  const handleFrequencyChange = (newFrequency: RepeatFrequency) => {
    if (newFrequency !== RepeatFrequency.Daily) {
      const firstDaySettings = Object.values(daysRequestBodyTemplates.value)[0];
      if (firstDaySettings) {
        singleRequestBodyTemplate.value = {
          ...firstDaySettings,
          dayOfWeek: undefined
        };
      }
      selectedDays.value = [];
      daysRequestBodyTemplates.value = {};
    }
    emitUpdate();
  };

  const initializeTemplates = (templates: EtradeApiRequestBodyTemplate[]) => {
    if (!templates || templates.length === 0) return;

    const hasDayOfWeek = templates.some(
      t => t.dayOfWeek !== undefined && t.dayOfWeek !== null
    );

    if (hasDayOfWeek) {
      daysRequestBodyTemplates.value = {};
      selectedDays.value = [];

      templates.forEach(template => {
        if (template.dayOfWeek !== undefined && template.dayOfWeek !== null) {
          daysRequestBodyTemplates.value[template.dayOfWeek] = template;
          selectedDays.value.push(template.dayOfWeek);
        }
      });

      selectedDays.value.sort((a, b) => a - b);
      if (selectedDays.value.length > 0) {
        activeDay.value = String(selectedDays.value[0]);
      }
    } else {
      singleRequestBodyTemplate.value = { ...templates[0] };
    }
  };

  const getTemplatesArray = (
    repeatFrequency: RepeatFrequency
  ): EtradeApiRequestBodyTemplate[] => {
    if (repeatFrequency === RepeatFrequency.Daily) {
      return Object.values(daysRequestBodyTemplates.value);
    } else {
      return [singleRequestBodyTemplate.value];
    }
  };

  return {
    selectedDays,
    activeDay,
    daysRequestBodyTemplates,
    singleRequestBodyTemplate,
    getDayLabel,
    selectAllDays,
    toggleDay,
    copyConfigToDay,
    addFilter,
    deleteFilter,
    updateApiRequestBodyField,
    handleFrequencyChange,
    initializeTemplates,
    getTemplatesArray
  };
};
